import { useState, useEffect, useCallback, RefObject } from 'react';

interface UseScrollHeaderOptions {
  threshold?: number;
  scrollContainerRef?: RefObject<HTMLElement>;
}

interface UseScrollHeaderReturn {
  isScrolled: boolean;
}

/**
 * Hook to detect when page has scrolled past a threshold
 * Used for header background/shadow transitions
 *
 * @param options.threshold - Scroll position (in pixels) to trigger the scrolled state (default: 50)
 * @param options.scrollContainerRef - Optional ref to a scroll container (defaults to window)
 * @returns Object with isScrolled boolean
 */
export const useScrollHeader = (options: UseScrollHeaderOptions = {}): UseScrollHeaderReturn => {
  const { threshold = 50, scrollContainerRef } = options;
  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = useCallback(() => {
    let scrollTop: number;

    if (scrollContainerRef?.current) {
      scrollTop = scrollContainerRef.current.scrollTop;
    } else {
      // Try to find the landing layout scroll container
      const scrollContainer = document.querySelector('[data-scroll-container="landing"]');
      scrollTop = scrollContainer ? scrollContainer.scrollTop : window.scrollY;
    }

    setIsScrolled(scrollTop > threshold);
  }, [threshold, scrollContainerRef]);

  useEffect(() => {
    // Check initial scroll position
    handleScroll();

    // Determine which element to listen to
    let scrollElement: HTMLElement | Window;

    if (scrollContainerRef?.current) {
      scrollElement = scrollContainerRef.current;
    } else {
      const scrollContainer = document.querySelector('[data-scroll-container="landing"]');
      scrollElement = (scrollContainer as HTMLElement) || window;
    }

    // Add scroll listener
    scrollElement.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, scrollContainerRef]);

  return { isScrolled };
};
